import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { RedirectService } from './redirect.service';

export const SecurityRoles = 'security-roles';
export const SecurityGroups = 'security-groups';
export const SecurityAuthenticate = 'security-authenticate';

/**
 * Protects routes based on the provided security-roles or security-groups data tags
 */
@Injectable()
export class AuthGuardService {
  constructor(private authService: AuthenticationService, private redirectService: RedirectService) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    return this.isAuthorized(route, state);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    return this.isAuthorized(route, state);
  }

  private isAuthorized(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    let authorized = false;
    let protectedByRoles = false;
    let protectedByGroups = false;
    let overrideRoute: string | undefined = undefined;
    let authenticate = true;

    // We need to have route data in order to specify roles and groups
    if (route.data) {
      overrideRoute = route.data['unauthorized-route'];

      // We will only check roles and groups if there is an authenticated user
      if (this.authService.isAuthenticated) {
        let roles = route.data[SecurityRoles] as Array<string>;
        let groups = route.data[SecurityGroups] as Array<string>;
        const user = this.authService.user;

        if (roles !== null && roles !== undefined && roles.length > 0) {
          protectedByRoles = true;
          console.debug(
            'Route protected by roles.\nRequired Roles:',
            roles,
            '\nRoute:',
            state.url,
            '\nUser Roles:',
            user ? user.roles : ''
          );

          authorized = this.authService.isRoleAuthorized(roles);
        }

        if (!authorized && groups !== null && groups !== undefined && groups.length > 0) {
          protectedByGroups = true;
          console.debug(
            'Route protected by groups.\nRequired groups:',
            groups,
            '\nRoute:',
            state.url,
            '\nUser Groups:',
            user ? user.groups : ''
          );

          authorized = this.authService.isGroupAuthorized(groups);
        }
      } else if (route.data[SecurityAuthenticate] === false) { // User should not authenticate
        authenticate = false;
      }
    }

    // If we did not have a role or group tag then we are required to be authenticated but
    // are not required to have any specific group or role
    if (!protectedByGroups && !protectedByRoles && this.authService.isAuthenticated) {
      authorized = true;
      console.debug('Route protected by authentication but does not restrict roles or groups.\nRoute:', state.url);
    }

    if (authorized) {
      console.debug('User' + (authorized ? ' ' : ' not ') + 'authorized');
    } else if (!this.authService.isAuthenticated && authenticate) {
      console.debug('User is not authenticated; redirecting the user to the logon page');
      this.authService.login(true, state.url);
    } else {
      console.debug('User' + (authorized ? ' ' : ' not ') + 'authorized; redirecting to forbidden path');
      this.redirectService.navigateToForbidden(overrideRoute);
    }

    return authorized;
  }
}
